import {
  Alert,
  Badge,
  EmptyState,
  Module,
  ModuleBody,
  ModuleGrid,
  StatusDot,
} from "@vjal/ui";
import type { ReactNode } from "react";
import type { RouteAccessDecision } from "../domain/capabilities";
import { getRoleDefinition, isReadOnlyRole, type CoachingRole } from "../domain/roles";
import { recipeProps, proTableDataAttrs } from "@vjal/screen-kit";
import type { SurfaceRecipeId } from "@vjal/screen-ir";
import {
  screenForRoute,
  type ProductScreenContract,
} from "./product-route-contracts";
import { ProductRouteOperationalShell } from "./product-route-operational-shell";

interface ProductRoutePageProps {
  role: CoachingRole;
  decision: RouteAccessDecision;
  routePath: string;
  stateOverride?: ReactNode;
  recipe?: SurfaceRecipeId;
}

// Phase 4: company_admin is promoted to "scoped" write posture (so the two
// follow-through capabilities flip on), but it stays READ-ONLY on every
// operational surface. The generic read-only banner is driven by
// isReadOnlyRole(), which is now false for company_admin — so we keep showing it
// for company_admin on operational routes, and suppress it only on the
// follow-through surfaces where it legitimately has actions.
const companyAdminActionRoutes = new Set(["org-admin-home", "org-reports", "report-detail", "reports", "session-report"]);

export function ProductRoutePage({ role, decision, routePath, stateOverride, recipe }: ProductRoutePageProps) {
  const route = decision.route;
  const roleDefinition = getRoleDefinition(role);

  if (!route) {
    return <FallbackSurface title="Unknown route" />;
  }

  const isReadOnly =
    isReadOnlyRole(role) || (role === "company_admin" && !companyAdminActionRoutes.has(route.id));

  const screen = screenForRoute(route.id);
  const CustomBody = screen.customBody;
  const title = screen.title(role);
  const isAuthSurface = route.id === "login";
  const bodyOwnsPrimaryH1 = !stateOverride && screen.bodyOwnsPrimaryH1?.(role) === true;
  const recipeDom =
    recipe === "ProTable-1"
      ? proTableDataAttrs()
      : recipe
        ? recipeProps(recipe)
        : undefined;

  return (
    <div
      className="mx-auto flex max-w-7xl flex-col gap-5"
      data-testid={`screen-${route.id}`}
      data-screen-id={route.id}
      {...recipeDom}
    >
      <ModuleGrid>
        <Module span={12} ink className="bg-grad-ink-hero">
          <ModuleBody className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <Badge variant="on-ink" className="mb-4 w-fit">
                {isAuthSurface ? "Secure access" : `${roleDefinition.label} workspace`}
              </Badge>
              {!isAuthSurface && (
                bodyOwnsPrimaryH1 ? (
                  <p className="m-0 max-w-4xl font-display text-h1 font-normal text-white">{title}</p>
                ) : (
                  <h1 className="m-0 max-w-4xl font-display text-h1 font-normal text-white">{title}</h1>
                )
              )}
              <p className="mt-4 max-w-3xl text-base leading-7 text-white/70">{screen.primaryJob}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2 text-sm text-white/80">
              <StatusDot tone={decision.recordScopeStatus === "in-scope" ? "ok" : "info"} />
              <span>Scoped view</span>
            </div>
          </ModuleBody>
        </Module>

        {isReadOnly && !isAuthSurface ? (
          <Module span={12}>
            <ModuleBody>
              <Alert variant="info">
                {roleDefinition.label} access is read-only. Any controls you can't use stay visible but disabled,
                with a short note explaining why.
              </Alert>
            </ModuleBody>
          </Module>
        ) : null}

        {stateOverride ? (
          stateOverride
        ) : (
          <>
            {isAuthSurface && CustomBody ? (
              <CustomBody role={role} decision={decision} routePath={routePath} />
            ) : !isAuthSurface ? (
              <OperationalSurface decision={decision} role={role} routePath={routePath} screen={screen} />
            ) : null}
          </>
        )}
      </ModuleGrid>
    </div>
  );
}

function OperationalSurface({
  decision,
  role,
  routePath,
  screen
}: {
  decision: RouteAccessDecision;
  role: CoachingRole;
  routePath: string;
  screen: ProductScreenContract;
}) {
  // The participant `/meetings` alias is an allowed legacy entry point, but its
  // body is the own-session view (screen-spec participant meetings rows 1560-1572),
  // not the staff operational shell with Bulk import/Create series/Export CSV.
  const isParticipantMeetings = role === "participant" && routePath === "/meetings";
  const isFocusedSurface = screen.surfaceMode === "focused" || isParticipantMeetings;
  const context = { role, decision, routePath };
  const CustomBody = screen.customBody;

  return (
    <>
      {CustomBody ? <CustomBody {...context} /> : null}
      {isFocusedSurface ? null : (
        <ProductRouteOperationalShell decision={decision} role={role} screen={screen} />
      )}
    </>
  );
}

function FallbackSurface({ title }: { title: string }) {
  return (
    <Module>
      <ModuleBody>
        <EmptyState title={title}>
          This route does not have a screen contract yet.
        </EmptyState>
      </ModuleBody>
    </Module>
  );
}
